import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardBody, Typography } from '@material-tailwind/react';
import { DEFAULT_COMPANY_INFO } from '../../../utils/companyInfo';
import DocumentPreviewScaler from '../owners/DocumentPreviewScaler';
import JobCardPrintSheet from './JobCardPrintSheet';
import { downloadJobCardPDF } from './jobCardPDFGenerator';
import { fetchPublicJobCardDocument } from './jobCardDocumentService';

const PublicJobDocumentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [jobCard, setJobCard] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadBusy, setDownloadBusy] = useState(false);
  const autoDownloadTriggered = useRef(false);
  const generatedBy = localStorage.getItem('cfms-name') || localStorage.getItem('username') || 'Public Viewer';

  const shouldAutoDownload = searchParams.get('download') === '1';

  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetchPublicJobCardDocument(token);

        if (cancelled) {
          return;
        }

        setJobCard(response.jobCard || null);
        setCompanyInfo(response.companyInfo || DEFAULT_COMPANY_INFO);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load document');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (token) {
      loadDocument();
    } else {
      setLoading(false);
      setError('Invalid share link');
    }

    return () => {
      cancelled = true;
    };
  }, [token]);

  const downloadPdf = useCallback(async () => {
    if (!jobCard) return;

    try {
      setDownloadBusy(true);
      await downloadJobCardPDF(jobCard, companyInfo, generatedBy);
    } finally {
      setDownloadBusy(false);
    }
  }, [jobCard, companyInfo, generatedBy]);

  useEffect(() => {
    if (!shouldAutoDownload || autoDownloadTriggered.current || !jobCard) {
      return;
    }

    autoDownloadTriggered.current = true;
    const timer = window.setTimeout(() => {
      downloadPdf().catch(() => {});
    }, 300);

    return () => window.clearTimeout(timer);
  }, [shouldAutoDownload, jobCard, downloadPdf]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-12">
        <Card className="mx-auto max-w-3xl border-0 shadow-lg">
          <CardBody className="p-8 text-center">
            <Typography variant="h5" className="text-[#2a2e30]">
              Loading document...
            </Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-12">
        <Card className="mx-auto max-w-3xl border-0 shadow-lg">
          <CardBody className="p-8 text-center">
            <Typography variant="h5" className="text-red-700">
              {error}
            </Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!jobCard) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-3 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Card className="border-0 shadow-xl">
          <CardBody className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Typography variant="h4" className="text-[#2a2e30]">
                  Job Card Document
                </Typography>
                <Typography variant="small" className="text-gray-500">
                  Public document link
                </Typography>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={downloadPdf}
                  disabled={downloadBusy}
                  className="border border-[#0f766e] bg-white text-[#0f766e] shadow-sm hover:bg-[#ecfdf5]"
                  style={{ backgroundColor: '#ffffff', color: '#0f766e' }}
                >
                  {downloadBusy ? 'Preparing...' : 'Download PDF'}
                </Button>
              </div>
            </div>

            <div className="max-h-[72vh] overflow-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
              <DocumentPreviewScaler>
                <JobCardPrintSheet jobCard={jobCard} companyInfo={companyInfo} generatedBy={generatedBy} />
              </DocumentPreviewScaler>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default PublicJobDocumentPage;
